import { FileIcon } from "@untitledui/file-icons";
import type { FC } from "react";

import { extensionOf, resolveFileIcon } from "@/features/caseFiles/fileKind";
import { cn } from "@/lib/utils";
import { thesvgIconUrl } from "@/utils/fileTypeIcon";

type Props = {
  /** File name (with extension); omit for folders. */
  name?: string;
  mimeType?: string;
  isFolder?: boolean;
  /** An Untitled UI type (`"pdf"`, `"docx"`…) when the caller already knows it. */
  type?: string;
  variant?: "default" | "gray" | "solid";
  /** Rendered width/height in px. */
  size?: number;
  className?: string;
};

const isDark = (): boolean =>
  typeof document !== "undefined" &&
  document.documentElement.classList.contains("dark");

/**
 * The one file-type icon for the whole app: thesvg brand marks for the formats
 * it draws well (folders, PDF, Word, Excel, PowerPoint) and Untitled UI for
 * images and everything else. Callers pass a name (and optional mime type),
 * a known `type`, or `isFolder`; the source is chosen by `resolveFileIcon`.
 */
export const FileTypeIcon: FC<Props> = ({
  name,
  mimeType,
  isFolder = false,
  type,
  variant = "default",
  size = 16,
  className
}) => {
  const icon = resolveFileIcon({
    name: name ?? (type ? `file.${type}` : undefined),
    mimeType,
    isFolder
  });
  if (icon.source === "thesvg" && variant === "default") {
    return (
      <img
        src={thesvgIconUrl(icon.slug)}
        alt=""
        width={size}
        height={size}
        className={cn("shrink-0 object-contain", className)}
      />
    );
  }
  return (
    <FileIcon
      type={
        icon.source === "untitled"
          ? icon.type
          : type ?? (extensionOf(name ?? "") || "empty")
      }
      variant={variant}
      theme={isDark() ? "dark" : "light"}
      size={size}
      className={cn("shrink-0", className)}
    />
  );
};
